/**
 * pushStore — WS 推送状态 + 事件分发中枢
 *
 * 不持久化任何字段
 *
 * ★ 避免循环依赖策略：
 *   其他 store 的 useXxxStore() 函数通过静态 import 引入，
 *   但只在 action 运行时调用（不在模块顶层调用），
 *   Pinia 保证循环引用在运行时安全（各 store 已注册后才执行 action）。
 */

import { defineStore } from 'pinia'
import { logger } from '@/utils/logger'
import { getEffectiveMachineCodeSync } from '@/utils/machineCode'
import { useToast } from '@/components/toast/useToast'
import { i18n } from '@/i18n'
import type { WsState, Envelope, BizEventType } from '@/ws/types'
import { formatWsAuthFailMessage } from '@/ws/authFailMessage'

// ─── 各业务事件 payload 类型 ──────────────────────────────────────────────────

interface AdUpdatedEnvelopePayload {
  ads: Array<{
    id: number
    type: 'image' | 'video'
    url: string
    durationMs: number
    weight?: number
  }>
}

/**
 * bill_rejected 事件 payload
 * R1 守护：严禁 raw_hex / bill_serial / dtu_id 等敏感字段
 */
interface BillRejectedEnvelopePayload {
  order_id?: string | number
  reason?: string
  rejected_at?: number
  /** 当前订单真实已付金额（元），后端权威值；前端用于回滚误累加的 paidAmount */
  total_paid?: number
  /** 被拒金额（元），用于 toast 显示 */
  amount?: number
}

interface InventoryLowEnvelopePayload {
  product_id: number
  remaining: number
  stock?: number
  threshold?: number
}

interface OrderStatusChangedEnvelopePayload {
  order_id: string | number
  status: string
  reason?: string
}

interface BillInsertedEnvelopePayload {
  order_id?: number
  delta_paid?: number
  amount?: number
  total_paid?: number
  cumulative?: number
  total?: number
  currency?: string
  /** 收钞续期后的订单过期时间（Unix 秒） */
  expires_at?: number
}

interface AuthOkEnvelopePayload {
  machine_code?: string
}

interface MachineCommandEnvelopePayload {
  command: string
  params?: Record<string, unknown>
  reason?: string
}

// ─── command → action 映射 ────────────────────────────────────────────────────

const COMMAND_MAP: Record<string, 'lock' | 'unlock' | 'reboot' | 'refresh' | 'shell_quit' | 'maintenance'> = {
  lock: 'lock',
  unlock: 'unlock',
  reboot: 'reboot',
  refresh: 'refresh',
  maintenance: 'maintenance',
  shell_quit: 'shell_quit',
}

// ─── 系统事件白名单（dispatch 时不报 unknown） ────────────────────────────────

const KNOWN_SYS_TYPES = new Set([
  'heartbeat', 'heartbeat_ack', 'ping', 'pong',
  'ack', 'auth_ok', 'auth_fail', 'error', 'hello',
])

// ─── 计数器初始值工厂 ─────────────────────────────────────────────────────────

function initCounter(): Record<BizEventType, number> {
  return {
    ad_updated: 0,
    ad_list_invalidated: 0,
    inventory_low: 0,
    order_status_changed: 0,
    order_fulfilled: 0,
    bill_inserted: 0,
    bill_rejected: 0,
    machine_command: 0,
    profit_updated: 0,
    profit_rule_changed: 0,
  }
}

// ─── Store 定义 ───────────────────────────────────────────────────────────────

export const usePushStore = defineStore('push', {
  state: () => ({
    /** 当前 WS 状态（不持久化） */
    connectionState: 'IDLE' as WsState,
    /** 最近收到事件时间（不持久化） */
    lastEventAt: 0,
    /** 各类事件计数（不持久化，调试用） */
    eventCounter: initCounter(),
    /** 最近错误（不持久化） */
    errorMessage: null as string | null,
    /** 拒绝原因（如 machine_not_found, machine_mode_mismatch） */
    rejectReason: null as string | null,
    /** WS auth_ok 绑定的机器码（服务端 UID，不持久化） */
    wsBoundMachineCode: null as string | null,
  }),

  getters: {
    isConnected: (state): boolean => state.connectionState === 'CONNECTED',

    isOnline: (state): boolean =>
      (['CONNECTED', 'AUTHENTICATING'] as WsState[]).includes(state.connectionState),

    /** 本地机器码与 WS 绑定机器码不一致（会导致投钞推送丢失） */
    machineCodeMismatch: (state): boolean => {
      const local = getEffectiveMachineCodeSync()
      const bound = state.wsBoundMachineCode
      return !!bound && !!local && bound !== local
    },

    totalEvents: (state): number =>
      Object.values(state.eventCounter).reduce((a, b) => a + b, 0),
  },

  actions: {
    setConnectionState(s: WsState): void {
      this.connectionState = s
      if (s === 'IDLE' || s === 'FAILED') {
        this.wsBoundMachineCode = null
      }
      if (s === 'CONNECTED') {
        this.errorMessage = null
        this.rejectReason = null
      }
      logger.info('[pushStore] WS 状态变更', { state: s })
    },

    setWsBoundMachineCode(code: string | null): void {
      this.wsBoundMachineCode = code?.trim() || null
      if (this.machineCodeMismatch) {
        logger.warn('[pushStore] 机器码不一致', {
          local: getEffectiveMachineCodeSync(),
          ws_bound: this.wsBoundMachineCode,
        })
      }
    },

    /**
     * 事件分发中枢
     * ★ 在 action 内动态 import 其他 store 实例，避免模块顶层循环引用问题
     */
    async dispatch(envelope: Envelope): Promise<void> {
      // ★ 大小写归一化：CardBillConsumer 推送大写（BILL_INSERTED），
      //   OrderService.emitPush 推送小写（bill_inserted）
      //   统一转为小写后再匹配，确保两种路径都能被正确处理
      const type = (envelope.type as string).toLowerCase() as BizEventType
      this.lastEventAt = Date.now()

      // 累加已知业务类型计数
      if (Object.prototype.hasOwnProperty.call(this.eventCounter, type)) {
        this.eventCounter[type] = (this.eventCounter[type] ?? 0) + 1
      }

      logger.info('[pushStore] 分发事件', { type, msg_id: envelope.msg_id })

      switch (type) {
        case 'ad_updated':
        case 'ad_list_invalidated': {
          const { useAdStore } = await import('./adStore')
          const adStore = useAdStore()
          if (type === 'ad_updated') {
            const payload = envelope.payload as AdUpdatedEnvelopePayload
            adStore.applyAdUpdated({ ads: payload.ads ?? [] })
          } else {
            adStore.fetchList().catch((err: unknown) => {
              logger.warn('[pushStore] ad_list_invalidated 拉取失败', { error: String(err) })
            })
          }
          break
        }

        case 'inventory_low': {
          const { useProductStore } = await import('./productStore')
          const productStore = useProductStore()
          const payload = envelope.payload as InventoryLowEnvelopePayload
          productStore.applyInventoryLow({
            product_id: payload.product_id,
            remaining: payload.remaining ?? payload.stock ?? 0,
          })
          break
        }

        case 'order_status_changed':
        case 'order_fulfilled': {
          const { useOrderStore } = await import('./orderStore')
          const orderStore = useOrderStore()
          const payload = envelope.payload as OrderStatusChangedEnvelopePayload
          // order_fulfilled 事件后端可能不带 status 字段，兜底为 'FULFILLED'
          const resolvedStatus =
            payload.status || (type === 'order_fulfilled' ? 'FULFILLED' : '')
          if (!resolvedStatus) {
            logger.warn('[pushStore] order_status_changed 缺少 status 字段，跳过', {
              order_id: payload.order_id,
            })
            break
          }
          orderStore.applyStatusChange({
            order_id: payload.order_id,
            status: resolvedStatus,
          })
          // 兜底：从 DB 同步 paid_amount（防止 bill_inserted 丢失或 WS 竞态）
          const orderId = Number(payload.order_id)
          if (orderId > 0) {
            orderStore.fetchDetail(orderId).catch((err: unknown) => {
              logger.warn('[pushStore] order_fulfilled fetchDetail 失败', { error: String(err) })
            })
          }
          break
        }

        case 'bill_inserted': {
          const { useOrderStore } = await import('./orderStore')
          const orderStore = useOrderStore()
          // 兼容后端不同版本的字段名 (delta_paid vs amount, total_paid vs cumulative/total)
          // ★ 后端所有路径（OrderService.emitPush / CardBillConsumer.pushWs）均以"元"为单位发送
          //   delta_paid / amount   → 单次投入（元），传入 applyBillInserted 前需 ×100 转分
          //   total_paid / cumulative / total → 累计已付（元），同样需 ×100 转分
          const payload = envelope.payload as BillInsertedEnvelopePayload
          const deltaYuan = Number(payload.delta_paid ?? payload.amount ?? 0)
          let totalYuan = Number(payload.total_paid ?? payload.cumulative ?? payload.total ?? 0)

          // 部分后端路径仅发送单次投入 amount，无 cumulative — 以 delta 作为累计
          if (totalYuan <= 0 && deltaYuan > 0) {
            totalYuan = deltaYuan
          }

          logger.info('[pushStore] 处理 bill_inserted', {
            deltaYuan,
            totalYuan,
            raw_payload_keys: Object.keys(payload as object),
          })

          // 只要 totalYuan > 0 就可以更新，因为后端可能只发累计值
          if (totalYuan > 0 || deltaYuan > 0) {
            orderStore.applyBillInserted({
              // applyBillInserted 期望分单位：pushStore 在此完成元→分的唯一转换点
              amount: Math.round(deltaYuan * 100),
              total_paid: Math.round(totalYuan * 100),
              expires_at: payload.expires_at,
            })
          } else {
            logger.warn('[pushStore] bill_inserted 数据无效，跳过更新', { deltaYuan, totalYuan })
          }
          break
        }

        case 'bill_rejected': {
          // R1 守护：不展示 raw_hex / bill_serial / dtu_id
          const payload = envelope.payload as BillRejectedEnvelopePayload
          const reason = payload.reason ?? 'unknown'
          // legacy:false Composition API 模式下 i18n.global 类型为 unknown，使用类型断言
          const tFn = (i18n.global as unknown as { t: (key: string) => string }).t
          useToast().error(tFn('pay.billRejected'))
          // 注意：eventCounter 由顶部公共逻辑统一累加（hasOwnProperty 判断），此处不重复 +1
          logger.warn('[pushStore] 纸钞拒收', {
            reason,
            order_id: payload.order_id ?? null,
          })

          // ★ 回滚 paidAmount：用后端权威 total_paid 修正前端已累加的金额
          //   场景：DTU 先上报投入 → BILL_INSERTED 推送（前端 paidAmount 增加）
          //         → 钞票被物理退出 → BILL_REJECTED 推送（前端需归零/回滚）
          //   策略：
          //   1. reason=no_active_order：Redis key 超时但订单仍在 IN_PAY，
          //      此时 total_paid=0 是错误值（后端无法定位订单），
          //      不应回滚前端 paidAmount；改用 fetchDetail 从 DB 同步真实值
          //   2. payload.total_paid > 0（新版后端，单位元）→ 直接转分同步
          //   3. payload.total_paid 不存在或为 0（旧版后端 / no_active_order）→ fetchDetail 从 DB 读取兜底
          {
            const { useOrderStore } = await import('./orderStore')
            const orderStore = useOrderStore()
            const isNoActiveOrder = reason === 'no_active_order'
            if (!isNoActiveOrder && typeof payload.total_paid === 'number' && payload.total_paid > 0) {
              // 后端直接告知当前真实累计（元→分）
              const totalPaidFen = Math.round(payload.total_paid * 100)
              orderStore.applyBillInserted({
                amount: 0,             // delta 无意义，用 total_paid 权威覆盖
                total_paid: totalPaidFen,
              })
              logger.info('[pushStore] bill_rejected：已按 total_paid 回滚 paidAmount', {
                total_paid_yuan: payload.total_paid,
                total_paid_fen: totalPaidFen,
              })
            } else if (orderStore.current) {
              // no_active_order（Redis key 超时）或旧版后端无 total_paid：
              // 触发 fetchDetail 从 DB 同步真实值（兜底），保留当前 paidAmount 不归零
              const orderId = Number(orderStore.current.id)
              orderStore.fetchDetail(orderId).catch((err: unknown) => {
                logger.warn('[pushStore] bill_rejected fetchDetail 失败', { error: String(err) })
              })
              logger.info('[pushStore] bill_rejected：触发 fetchDetail 兜底同步', {
                orderId,
                reason,
                isNoActiveOrder,
              })
            } else {
              logger.warn('[pushStore] bill_rejected：无当前订单，跳过回滚', { reason })
            }
          }
          break
        }

        case 'machine_command': {
          const { useMachineStore } = await import('./machineStore')
          const machineStore = useMachineStore()
          const payload = envelope.payload as MachineCommandEnvelopePayload
          const mappedAction = COMMAND_MAP[payload.command]
          if (mappedAction) {
            machineStore.applyMachineCommand({
              action: mappedAction,
              reason: payload.reason ?? (payload.params?.reason as string | undefined),
            })
          } else {
            logger.warn('[pushStore] 未知 machine_command', { command: payload.command })
          }
          break
        }

        case 'profit_rule_changed':
        case 'profit_updated': {
          const { useProductStore } = await import('./productStore')
          const productStore = useProductStore()
          productStore.applyProfitRuleChanged().catch((err: unknown) => {
            logger.warn('[pushStore] profit_rule_changed 拉取失败', { error: String(err) })
          })
          break
        }

        default: {
          if (type === 'auth_ok') {
            const payload = envelope.payload as AuthOkEnvelopePayload
            if (payload.machine_code) {
              this.setWsBoundMachineCode(payload.machine_code)
            }
            break
          }
          if (type === 'auth_fail') {
            const payload = envelope.payload as { reason?: string }
            const reason = String(payload.reason || 'unknown')
            this.recordError(formatWsAuthFailMessage(reason), reason)
            break
          }
          if (type === 'error') {
            const payload = envelope.payload as { reason?: string; error?: string }
            const reason = String(payload.reason || 'ws_error')
            const detail = payload.error ? ` (${payload.error})` : ''
            this.recordError(formatWsAuthFailMessage(reason) + detail, reason)
            break
          }
          if (!KNOWN_SYS_TYPES.has(type)) {
            const msg = `[pushStore] 未知事件类型: ${type}`
            this.recordError(msg)
            logger.warn(msg, { envelope_type: type })
          }
        }
      }
    },

    recordError(msg: string, reason?: string): void {
      this.errorMessage = msg
      if (reason) this.rejectReason = reason
      logger.warn('[pushStore] 错误记录', { msg, reason })
    },

    resetCounter(): void {
      this.eventCounter = initCounter()
    },
  },

  persist: false,
})
